import { Component, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { AuthService } from '../../../../services/auth.service';
import { EstablishmentService } from '../../../../services/establishment.service';
import { FlagService } from '../../../../shared/services/flag.service';
import { FlagType } from '../../../../shared/utils/enums';

import { FirstStepAddEstablishmentComponent } from './first-step-add-establishment/first-step-add-establishment.component';
import { SecondStepAddEstablishmentComponent } from './second-step-add-establishment/second-step-add-establishment.component';
import { ThirdStepAddEstablishmentComponent } from './third-step-add-establishment/third-step-add-establishment.component';
import { FinalStepAddEstablishmentComponent } from './final-step-add-establishment/final-step-add-establishment.component';

@Component({
  selector: 'app-add-establishment-dialog',
  standalone: true,
  templateUrl: './add-establishment-dialog.component.html',
  styleUrls: ['./add-establishment-dialog.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    FirstStepAddEstablishmentComponent,
    SecondStepAddEstablishmentComponent,
    ThirdStepAddEstablishmentComponent,
    FinalStepAddEstablishmentComponent
  ]
})
export class AddEstablishmentDialogComponent {
  step = 1;
  loading = false;

  establishmentForm: FormGroup;

  @ViewChild(FirstStepAddEstablishmentComponent) firstStepComponent?: FirstStepAddEstablishmentComponent;
  @ViewChild(SecondStepAddEstablishmentComponent) secondStepComponent?: SecondStepAddEstablishmentComponent;
  @ViewChild(ThirdStepAddEstablishmentComponent) thirdStepComponent?: ThirdStepAddEstablishmentComponent;
  @ViewChild(FinalStepAddEstablishmentComponent) finalStepComponent?: FinalStepAddEstablishmentComponent;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AddEstablishmentDialogComponent>,
    private authService: AuthService,
    private establishmentService: EstablishmentService,
    private flag: FlagService,
    private cdr: ChangeDetectorRef
  ) {
    this.establishmentForm = this.fb.group({
      firstStep: this.fb.group({
        //prefixe: ['', Validators.required],
        //firstName: ['', Validators.required],
        //lastName: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        //password: ['', [Validators.required]],
        // confirmBtn: [false, Validators.requiredTrue]
      }),
      secondStep: this.fb.group({
        name: ['', Validators.required],
        numsiret: ['', [Validators.required, Validators.pattern(/^\d{14}$/)]],
        typeetablissement: ['', Validators.required],
        nombresalaries: [''],
        nombreannuelpatients: [''],
        numfiness: ['', [Validators.pattern(/^\d{9}$/)]],
        description: [''],
        platformManagedBy: ['']
      }),
      thirdStep: this.fb.group({
        adresses: this.fb.array([])
      }),
      finalStep: this.fb.group({
        persons: this.fb.array([])
      })
    });
  }

  get firstStepFormGroup(): FormGroup {
    return this.establishmentForm.get('firstStep') as FormGroup;
  }
  get secondStepFormGroup(): FormGroup {
    return this.establishmentForm.get('secondStep') as FormGroup;
  }
  get thirdStepFormGroup(): FormGroup {
    return this.establishmentForm.get('thirdStep') as FormGroup;
  }
  get finalStepFormGroup(): FormGroup {
    return this.establishmentForm.get('finalStep') as FormGroup;
  }

  close(): void {
    this.dialogRef.close();
  }

  back(): void {
    if (this.step > 1) {
      this.step--;
      this.cdr.detectChanges();
    }
  }

  next(): void {
    if (this.step < 4) {
      this.step++;
      this.cdr.detectChanges();
    } else {
      this.submitEstablishment();
    }
  }

  submitEstablishment(): void {
    this.loading = true;
    // Fusionner les valeurs comme dans collary-team
    const finalBody = {
      ...this.establishmentForm.get('firstStep')?.value,
      ...this.establishmentForm.get('secondStep')?.value,
      ...this.establishmentForm.get('thirdStep')?.value,
      ...this.establishmentForm.get('finalStep')?.value,
    };
    finalBody.fullname = finalBody.firstName + ' ' + finalBody.lastName;

    if (finalBody.adresses) {
      finalBody.adresses = finalBody.adresses.map((addr: any) => {
        if (addr.photo1 === '') addr.photo1 = null;
        if (addr.photo2 === '') addr.photo2 = null;
        return addr;
      });
    }

    if (finalBody.persons) {
      finalBody.persons = finalBody.persons.map((a: any) => {
        const item = a;
        item.numTel = a?.numTel?.number || a?.numTel;
        item.fullnamepersonnecontact = a?.firstName + ' ' + a?.lastName;
        item.fonctionpersonnecontact = a?.fonction;
        item.photo1 = a.contactPhoto1 === '' ? null : a.contactPhoto1;
        item.photo2 = a.contactPhoto2 === '' ? null : a.contactPhoto2;
        delete item.contactPhoto1;
        delete item.contactPhoto2;
        return item;
      });
    }
    const establishmentId = this.authService.id;
    if (!establishmentId) {
      this.flag.displayFlag('ID de l\'établissement non trouvé', FlagType.error);
      this.loading = false;
      return;
    }
    this.establishmentService.updateEstablishment(finalBody, establishmentId).subscribe({
      next: (res) => {
        this.flag.displayFlag(
          'Compte établissement créé avec succès !',
          FlagType.success
        );
        localStorage.setItem('temp_establishment', JSON.stringify(res));
        localStorage.setItem('temp_id', res?.id);
        // Close the dialog with a special value to indicate redirection
        this.dialogRef.close({refresh: true, establishmentId: res?.id});
      },
      error: (err) => {
        console.log(err);
        this.flag.displayFlag(
          'Veuillez compléter les informations manquantes.',
          FlagType.error
        );
        this.loading = false;
      },
      complete: () => (this.loading = false)
    });
  }

  get isCurrentStepInvalid(): boolean {
    switch (this.step) {
      case 1:
        return this.firstStepFormGroup?.invalid ?? true;
      case 2:
        return this.secondStepFormGroup?.invalid ?? true;
      case 3:
        return this.thirdStepFormGroup?.invalid ?? true;
      case 4:
        return this.finalStepFormGroup?.invalid ?? true;
      default:
        return false;
    }
  }

  // Pour initialiser les adresses/persons si besoin
  get thirdStepAdresses(): FormArray {
    return this.establishmentForm.get('thirdStep.adresses') as FormArray;
  }
  get finalStepPersons(): FormArray {
    return this.establishmentForm.get('finalStep.persons') as FormArray;
  }
}
